import { SuspenseLoadingStatus } from '@smx-ui-common/suspense-loading-status';
import { getModel } from '@smx-ui-model/store-model';
import { ModelType, StoreModelType, TableResourceType } from '@smx-ui-model/types';
import { TablePaginationConfig } from 'antd';
import React, { forwardRef, Suspense, useEffect, useState } from 'react';

import { TableEntityBase } from './table-entity-base';
import { WrapperTableLoaderQuery } from './wrapper-table-loader-query';

//FIXME: simplify type
export type FilterOperatorType = 'EQ' | 'NE' | 'LT' | 'LTE' | 'GT' | 'GTE' | 'CONTAINS' | 'IN';
export type FilterType = {
    operator: FilterOperatorType;
    value: string | number;
};

interface TableEntityProps<T, C> {
    modelName?: keyof StoreModelType;
    model?: ModelType<T>;
    onSelectedRow?: (selectedRows: string[]) => void;
    inputFilter: Partial<{ [key in keyof T]: FilterType }>;
    limit?: number;
    input?: any;
    contextName?: C;
    fetchKeyForUpdate?: number;
}

export const TableEntity = forwardRef(
    <T, C>(
        {
            modelName,
            onSelectedRow,
            inputFilter,
            input,
            model,
            limit = 100,
            contextName,
            fetchKeyForUpdate = 0,
        }: TableEntityProps<T, C>,
        ref: React.Ref<any>,
    ) => {
        const [dataSource, setDataSource] = useState<TableResourceType[]>();
        const [isLoading, setIsLoading] = useState<boolean>(false);
        const [dataPageInfo, setDataPageInfo] = useState<any>();
        const [fetchKey, setFetchKey] = useState<number>(fetchKeyForUpdate);
        const [pagination, setPagination] = useState<TablePaginationConfig>({
            current: 1,
            pageSize: 10,
            total: 0,
        });

        useEffect(() => {
            setFetchKey(fetchKeyForUpdate);
        }, [fetchKeyForUpdate]);

        const internalModel = modelName ? getModel<ModelType<T>>(modelName) : model!;

        const newInput = {
            ...input,
            filter: inputFilter || {},
            page: pagination.current,
            limit: pagination.pageSize || limit,
        };

        const variables = {
            input: newInput,
        };

        useEffect(() => {
            setPagination({
                ...pagination,
                pageSize: limit,
            });
        }, [limit]);

        useEffect(() => {
            if (dataPageInfo) {
                setPagination({
                    ...pagination,
                    pageSize: dataPageInfo.limit,
                    total: dataPageInfo.totalCount,
                });
            }
        }, [dataPageInfo]);

        return (
            <>
                <Suspense
                    fallback={
                        <SuspenseLoadingStatus
                            onStart={() => {
                                setIsLoading(true);
                            }}
                            onEnd={() => {
                                setIsLoading(false);
                            }}
                        />
                    }
                >
                    <WrapperTableLoaderQuery
                        model={internalModel}
                        variables={variables}
                        setDataSource={setDataSource}
                        setDataPageInfo={setDataPageInfo}
                        fetchKey={fetchKey}
                    />
                </Suspense>

                <TableEntityBase<C>
                    ref={ref}
                    entityName={internalModel.name}
                    dataSource={dataSource}
                    isCheckBoxColumn={internalModel.isCheckBoxColumn}
                    onSelectedRow={onSelectedRow}
                    pagination={pagination}
                    setFetchKey={setFetchKey}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    contextName={contextName}
                />
            </>
        );
    },
) as <T, C>(props: TableEntityProps<T, C> & { ref?: React.Ref<any> }) => React.ReactElement;
